import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from "react";
import type Rollbar from "rollbar";
import { whenRollbarReady } from "./lazyRollbar";

type RollbarInstanceProviderContextValue = Rollbar | undefined;

const RollbarInstanceProviderContext =
  React.createContext<RollbarInstanceProviderContextValue>(undefined);

/**
 * Exposes the lazily-initialized Rollbar instance (see lazyRollbar.ts).
 * The context value is undefined during SSR and until the SDK loads on idle;
 * consumers already handle that (and can fall back to reportError, which
 * queues until the SDK is ready).
 */
export const RollbarInstanceProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const [rollbar, setRollbar] = useState<Rollbar | undefined>(undefined);

  useEffect(() => whenRollbarReady(setRollbar), []);

  return (
    <RollbarInstanceProviderContext.Provider value={rollbar}>
      {children}
    </RollbarInstanceProviderContext.Provider>
  );
};

export const useRollbarInstance = () => {
  return useContext(RollbarInstanceProviderContext);
};
